import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/ws3n6jq0_C%20-%20CENTURION.webp";

// Reference: Timeline footer (brand block | link columns | newsletter signup)
export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Shop", items: [
      { label: "Peptides", to: "/" },
      { label: "Blends", to: "/" },
      { label: "Cofactors", to: "/" },
      { label: "Research Supplies", to: "/" },
    ] },
    { title: "Support", items: [
      { label: "FAQs", to: "/faq" },
      { label: "Our Standards", to: "/standards" },
      { label: "Shipping & Returns", to: "/returns" },
      { label: "Contact Us", to: "/faq" },
    ] },
    { title: "Company", items: [
      { label: "About", to: "/faq" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Disclaimer", to: "/terms" },
      { label: "Privacy", to: "/terms" },
    ] },
  ];

  return (
    <footer className="bg-[#0A0F26] text-white border-t border-white/12" data-testid="main-footer">
      {/* Newsletter */}
      <div className="border-b border-white/12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-4">
              Newsletter
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tighter uppercase leading-none">
              Join the<br />research list
            </h2>
          </div>
          <form onSubmit={subscribe} className="w-full" data-testid="newsletter-form">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ENTER YOUR EMAIL"
                className="flex-1 bg-[#0C1128] border border-white/25 text-white px-5 py-4 text-xs tracking-[0.15em] uppercase outline-none focus:border-electric placeholder:text-white/30 sq-sm"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="col-span-2">
          <img src={LOGO} alt="Century Labs" className="h-12 w-12 object-contain mb-4 mix-blend-screen" />
          <div className="font-display font-bold text-2xl tracking-tighter uppercase">
            Century Labs
          </div>
          <p className="text-sm text-white/50 mt-4 max-w-xs leading-relaxed">
            Research-grade peptides and compounds. Lab-tested, with a real certificate of analysis
            on every batch.
          </p>
        </div>
        {columns.map((col) => (
          <div key={col.title}>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-5">
              {col.title}
            </p>
            <ul className="space-y-3">
              {col.items.map((it) => (
                <li key={it.label}>
                  <Link
                    to={it.to}
                    className="text-sm text-white/70 hover:text-electric cursor-pointer transition-colors duration-200"
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Legal disclaimer + hours */}
      <div className="border-t border-white/12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-3">Disclaimer</p>
            <p className="text-xs text-white/45 leading-relaxed max-w-3xl" data-testid="footer-disclaimer">
              These products are intended for laboratory research purposes only, and are not for human or animal
              consumption. They are not intended to diagnose, treat, cure, or prevent any disease, and should not be
              used as a food, drug, cosmetic, or for any household use. By accessing this website or purchasing from
              it, you agree that it is your sole responsibility to ensure compliance with all applicable laws and
              regulations within your jurisdiction, and you acknowledge that no information on this website constitutes
              medical advice. You acknowledge you have read and agree to the Terms of Service. Century Labs is a
              chemical supplier and is not a compounding pharmacy or chemical compounding facility as defined under
              Section 503A of the Federal Food, Drug, and Cosmetic Act, nor an outsourcing facility as defined under
              Section 503B of the same Act.
            </p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-3">Hours</p>
            <div className="text-xs text-white/60 leading-relaxed space-y-3">
              <div>
                <span className="text-white/40 uppercase tracking-[0.15em] text-[10px]">Shipping</span>
                <br />
                Mon to Fri · 9 AM to 5 PM EST
              </div>
              <div>
                <span className="text-white/40 uppercase tracking-[0.15em] text-[10px]">Support (7 Days)</span>
                <br />
                Mon to Fri · 9 AM to 5 PM EST
                <br />
                Sat to Sun · Ad hoc
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-3 text-[10px] tracking-[0.2em] uppercase text-white/40">
          <span>© {new Date().getFullYear()} Century Labs. All rights reserved. · For research use only · 21+</span>
          <div className="flex gap-6">
            <Link to="/faq" className="cursor-pointer hover:text-electric" data-testid="footer-faq-link">FAQ</Link>
            <Link to="/terms" className="cursor-pointer hover:text-electric">Privacy</Link>
            <Link to="/terms" className="cursor-pointer hover:text-electric">Terms</Link>
            <a href="/admin/login" className="hover:text-electric" data-testid="footer-admin-link">
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
